import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Users,
} from 'lucide-react';
import {
  LEGAL_CATEGORY_LABELS,
  fetchAcceptanceStatsForLegalPolicyVersion,
  fetchAcceptancesForLegalPolicyVersion,
  fetchPendingVendorsForLegalPolicyVersion,
  formatScopedVendorType,
  type LegalPolicyAcceptanceStats,
  type VendorPolicyAcceptanceLegalSnippet,
  type VendorPolicyAcceptanceRow,
  type VendorPolicyAcceptanceVendorDetails,
} from '../../services/MasterAdmin/legalPolicyVersionsService';
import { vendorRoleMapping } from '../../services/MasterAdmin/vendorService';

const TAB_ACCEPTED = 'ACCEPTED';
const TAB_PENDING = 'PENDING';
type TabId = typeof TAB_ACCEPTED | typeof TAB_PENDING;

const errorMessage = (e: unknown): string => {
  if (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
  ) {
    return String((e as { response?: { data?: { message?: string } } }).response?.data?.message);
  }
  return 'Unexpected error.';
};

const formatVendorRole = (role: number | null | undefined): string => {
  if (role === null || role === undefined) return '—';
  return vendorRoleMapping[String(role)] || `Role ${role}`;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch {
    return '—';
  }
};

const LegalPolicyAcceptancesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [version, setVersion] = useState<VendorPolicyAcceptanceLegalSnippet | null>(null);
  const [stats, setStats] = useState<LegalPolicyAcceptanceStats | null>(null);
  const [acceptedRows, setAcceptedRows] = useState<VendorPolicyAcceptanceRow[]>([]);
  const [pendingVendors, setPendingVendors] = useState<VendorPolicyAcceptanceVendorDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>(TAB_ACCEPTED);

  const load = useCallback(async () => {
    if (!id) {
      setLoadError('Missing policy id.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const [acceptedResp, statsResp] = await Promise.all([
        fetchAcceptancesForLegalPolicyVersion(id, { limit: 500, offset: 0 }),
        fetchAcceptanceStatsForLegalPolicyVersion(id),
      ]);
      setVersion(statsResp.legalPolicyVersion || acceptedResp.legalPolicyVersion);
      setStats(statsResp.stats);
      setAcceptedRows(acceptedResp.data || []);

      if ((statsResp.legalPolicyVersion || acceptedResp.legalPolicyVersion)?.audience === 'VENDOR') {
        try {
          const pendingResp = await fetchPendingVendorsForLegalPolicyVersion(id, {
            limit: 1000,
            offset: 0,
          });
          setPendingVendors(pendingResp.data || []);
        } catch {
          setPendingVendors([]);
        }
      } else {
        setPendingVendors([]);
      }
    } catch (e) {
      setLoadError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const headerSubtitle = useMemo(() => {
    if (!version) return null;
    const parts = [
      LEGAL_CATEGORY_LABELS[version.category],
      `v${version.version}`,
      version.audience,
    ];
    if (version.audience === 'VENDOR') {
      parts.push(formatScopedVendorType(version.scopedVendorType));
    }
    return parts.join(' · ');
  }, [version]);

  return (
    <div className="w-full min-w-0 max-w-6xl mx-auto space-y-6 p-3 sm:p-4 md:p-6">
      <Link
        to="/operations/legal-policies"
        className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to list
      </Link>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Vendor acceptances</h1>
          </div>
          {headerSubtitle && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{headerSubtitle}</p>
          )}
          {version?.title && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{version.title}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loadError && (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {loading && !version ? (
        <div className="flex justify-center py-12 text-gray-500 dark:text-gray-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        version && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <StatCard
                title="Accepted"
                value={stats?.acceptedDistinctVendors ?? stats?.acceptedCount ?? 0}
                icon={<CheckCircle2 className="h-4 w-4 text-green-600" />}
              />
              <StatCard
                title={version.audience === 'VENDOR' ? 'Expected vendors' : 'Records'}
                value={
                  version.audience === 'VENDOR'
                    ? stats?.expectedVendorCount ?? '—'
                    : stats?.acceptedCount ?? 0
                }
                icon={<Users className="h-4 w-4 text-blue-600" />}
              />
              <StatCard
                title="Pending"
                value={
                  version.audience === 'VENDOR'
                    ? stats?.pendingVendorCount ?? '—'
                    : '—'
                }
                icon={<Clock className="h-4 w-4 text-amber-600" />}
              />
            </div>

            {version.audience === 'VENDOR' && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setTab(TAB_ACCEPTED)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium ${
                    tab === TAB_ACCEPTED
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  Accepted ({acceptedRows.length.toLocaleString()})
                </button>
                <button
                  type="button"
                  onClick={() => setTab(TAB_PENDING)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium ${
                    tab === TAB_PENDING
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  Pending ({pendingVendors.length.toLocaleString()})
                </button>
              </div>
            )}

            {tab === TAB_ACCEPTED ? (
              <AcceptedTable rows={acceptedRows} />
            ) : (
              <PendingTable rows={pendingVendors} />
            )}
          </>
        )
      )}
    </div>
  );
};

const StatCard: React.FC<{ title: string; value: number | string; icon: React.ReactNode }> = ({
  title,
  value,
  icon,
}) => {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        {icon}
        <span>{title}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
};

const AcceptedTable: React.FC<{ rows: VendorPolicyAcceptanceRow[] }> = ({ rows }) => {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr>
              <Th>Vendor</Th>
              <Th>Role</Th>
              <Th>Accepted at</Th>
              <Th>Device (UA)</Th>
              <Th>Title snapshot</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  <div className="font-medium">{row.vendor?.email || row.vendorId}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {row.vendor?.generatedId || row.vendorId}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                  {formatVendorRole(row.vendor?.vendorRole ?? null)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                  {formatDate(row.acceptedAt)}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">
                  <div className="line-clamp-2 max-w-xs break-words">
                    {row.acceptedUserAgent || '—'}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                  {row.acceptedTitle || row.legalPolicyVersion?.title || '—'}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  No vendor has accepted this version yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PendingTable: React.FC<{ rows: VendorPolicyAcceptanceVendorDetails[] }> = ({ rows }) => {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr>
              <Th>Vendor</Th>
              <Th>Role</Th>
              <Th>Phone</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map((v) => (
              <tr key={v.vendorId} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  <div className="font-medium">{v.email}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{v.generatedId}</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                  {formatVendorRole(v.vendorRole)}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-200">{v.phoneNumber}</td>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">{v.status}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  No pending vendors in scope.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
    {children}
  </th>
);

export default LegalPolicyAcceptancesPage;
